"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Scene = void 0;
const blocks_1 = require("../blocks");
const pixi_js_1 = require("pixi.js");
const SceneManager_1 = require("../managers/SceneManager");
const Global_1 = require("../Global");
const gsap_1 = require("gsap");
const i18next_1 = __importDefault(require("i18next"));
const settings_1 = require("../config/settings");
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class Scene extends blocks_1.Block {
    constructor(app, sceneManager, gameManager, backgroundColor = null) {
        super(app);
        this.sceneManager = sceneManager;
        this.gameManager = gameManager;
        this.color = settings_1.Settings.whiteColor;
        this.inited = false;
        this.deinited = false;
        this.crash = false;
        this._musicVolume = 0;
        this.color = backgroundColor || settings_1.Settings.whiteColor;
        this.background = this.makeBackground();
        this.musicVolume = settings_1.Settings.defaultMusicVolume; // Default is a bit solfter, can override
    }
    set musicVolume(volume) {
        this._musicVolume = volume;
    }
    get musicVolume() {
        return this._musicVolume;
    }
    get bgoptions() {
        return {
            backgroundColor: this.color,
            border: 0,
            radius: 0,
            height: this.app.screen.height / (0, Global_1.px)(),
            width: this.app.screen.width / (0, Global_1.px)()
        };
    }
    makeBackground() {
        const bg = this.makeBox(this.bgoptions);
        bg.name = 'background';
        return this.addChild(bg);
    }
    makeFooterText(key) {
        const container = new pixi_js_1.Container();
        const txt = i18next_1.default.t(key);
        const style = blocks_1.Block.textStyle(this.overSmallSize ? 30 : 28);
        style.wordWrap = true;
        style.wordWrapWidth = this.app.screen.width;
        style.fill = settings_1.Settings.grayColor;
        style.align = 'center';
        const text = new pixi_js_1.Text(txt, style);
        text.anchor.set(0.5, 0.5);
        container.addChild(text);
        return this.addChild(container);
    }
    animateFooterText(container) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.promisedTimeline(gsap_1.gsap.timeline()
                .to(container, {
                pixi: {
                    positionY: this.app.screen.height - container.height + (0, Global_1.px)(15),
                    alpha: 1.0
                },
                ease: 'power2.out',
                duration: 0.5
            }));
        });
    }
    animateFall() {
        return __awaiter(this, void 0, void 0, function* () {
            // Fall down
            this.position.y = 0;
            return yield this.promisedTimeline(gsap_1.gsap.timeline()
                .to(this, {
                pixi: {
                    positionY: this.app.screen.height
                },
                ease: 'power3.in',
                duration: 0.5
            }));
        });
    }
    animateMask(target, reverse = false, duration = 0.5) {
        return __awaiter(this, void 0, void 0, function* () {
            let bnds = target;
            if (bnds instanceof pixi_js_1.DisplayObject) {
                bnds = bnds.getBounds();
            }
            const tbounds = {
                x: bnds.x,
                y: bnds.y,
                radius: 4,
                width: bnds.width,
                height: bnds.height
            };
            const sbounds = {
                x: 0,
                y: 0,
                radius: 0,
                width: this.app.screen.width,
                height: this.app.screen.height,
            };
            return yield new Promise((resolve) => {
                // Add a filter to fade our scene out
                const alphaFilter = new pixi_js_1.AlphaFilter();
                alphaFilter.alpha = 1.0;
                this.filters = [alphaFilter];
                const maskGraphic = new pixi_js_1.Graphics();
                maskGraphic.name = 'maskgraphic';
                this.mask = maskGraphic;
                this.addChild(maskGraphic);
                const vars = {
                    ease: 'power2.out',
                    duration: duration
                };
                let bounds;
                if (reverse) {
                    bounds = sbounds;
                    tbounds.alpha = 0.0;
                    bounds.alpha = 1.0;
                    for (let i in tbounds) {
                        vars[i] = tbounds[i];
                    }
                }
                else {
                    bounds = tbounds;
                    for (let i in sbounds) {
                        vars[i] = sbounds[i];
                    }
                }
                gsap_1.gsap.timeline({
                    autoRemoveChildren: true,
                    onComplete: () => {
                        this.mask = null;
                        this.removeChild(maskGraphic);
                        return resolve(true);
                    },
                    onUpdate: () => {
                        var _a;
                        alphaFilter.alpha = (_a = bounds.alpha) !== null && _a !== void 0 ? _a : 1.0;
                        maskGraphic.clear()
                            .beginFill(0xffffff)
                            .drawRoundedRect(bounds.x, bounds.y, bounds.width, bounds.height, (0, Global_1.px)(2));
                    }
                }).to(bounds, vars);
            });
        });
    }
    /**
     * Called when the transition has finished, and we are now the main Scene, good for making API calls
     * as well as some animations that were not a part of the transition
     */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            this.inited = true;
        });
    }
    get isInited() {
        return this.inited;
    }
    /**
     * Called when the transition is about to happen (before new Scene has been created)
     */
    deinit() {
        return __awaiter(this, void 0, void 0, function* () {
            this.deinited = true;
        });
    }
    get isDeinited() {
        return this.deinited;
    }
    /**
     * Called when we are entering the stage, will only fire the transtion has finished if returned true
     * @param from
     * @param target
     */
    enterScene(from, target) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, Global_1.setThemeColor)(this.color);
            if (AssetManager_1.default.backgroundGain && this.musicVolume !== AssetManager_1.default.backgroundGain.gain.value) {
                this.promisedTimeline(gsap_1.gsap.timeline({ autoRemoveChildren: true })
                    .to(AssetManager_1.default.backgroundGain.gain, {
                    value: this.musicVolume,
                    duration: 0.5
                })).then();
            }
            return true;
        });
    }
    /**
     *  Called when we are no longer needed on the stage, at this point the new Scene has already entered
     * @param to
     * @param target
     */
    exitScene(to, target) {
        return __awaiter(this, void 0, void 0, function* () {
            return true;
        });
    }
    crashScene() {
        return __awaiter(this, void 0, void 0, function* () {
            this.crash = true;
            return yield this.sceneManager.routeTo(this.backTo(), null);
        });
    }
    get triggersStart() {
        return false;
    }
    get triggersStop() {
        return false;
    }
    backTo() {
        return SceneManager_1.Scenes.Home;
    }
    layout() {
        super.layout();
        // Update our background to fit screen
        if (this.background instanceof pixi_js_1.Graphics) {
            this.makeBox(this.bgoptions, this.background);
        }
    }
}
exports.Scene = Scene;
